import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Archive, ArchiveRestore, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { EstadoVacio, Seccion } from '@/components/Card';
import { DateField, TextField } from '@/components/Field';
import { Fila, Filas } from '@/components/Fila';
import { Segmentado } from '@/components/Segmentado';
import { UnitField } from '@/components/UnitField';
import { actualizarSettings, leerSettings } from '@/db/schema';
import {
  NOMBRE_BASCULA_MAX,
  archivarBascula,
  borrarBascula,
  crearBascula,
  editarBascula,
  elegirBasculaDeTendencia,
  leerBasculasConPesajes,
  type BasculaConPesajes,
} from '@/db/basculas';
import { NOTA_PESAJE_MAX, borrarPesaje, contarPesajesIlegibles, guardarPesaje, leerPesajes } from '@/db/pesajes';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { Scale, Settings, WeighIn } from '@/db/types';
import { etiquetaMes, type FechaLocal } from '@/domain/dates';
import { fechaDelDia } from '@/domain/hoy';
import { formatearNumero } from '@/domain/numbers';
import { validarEstatura } from '@/domain/perfil';
import {
  DIFERENCIA_UNIDAD_KG,
  preguntaDeUnidad,
  referenciaParaUnidad,
  textoPreguntaDeUnidad,
  validarPeso,
  type PreguntaDeUnidad,
} from '@/domain/tendencia';
import { horaActual } from '@/domain/time';
import {
  desdeCm,
  desdeKg,
  esUnidadLongitud,
  esUnidadPeso,
  formatearCapturado,
  formatearPeso,
  UNIDADES_PESO,
  type Capturado,
  type UnidadLongitud,
  type UnidadPeso,
} from '@/domain/units';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { CampoHora, Opciones } from '@/features/food/comunes';
import { AvisoIlegibles, cuandoYDonde } from './BodyPage';

const VOLVER_A_CUERPO = { a: '/cuerpo', texto: 'Cuerpo', etiqueta: 'Volver a Cuerpo' };

/**
 * Un número como se escribe en un campo: sin comas de miles ni ceros
 * sobrantes, y sin recortar lo guardado. Con 2 decimales, abrir un pesaje
 * de 154.375 lb y guardar solo la nota lo cambiaba a 154.38 (revisión de la
 * 0.4.0); además, lo que no se cambia se guarda tal como estaba.
 */
const paraCampo = (v: number) => formatearNumero(v, { decimales: 6, fijos: false }).replace(/,/g, '');

/** Lo capturado de un pesaje, si está bien; un respaldo editado a mano podría no traerlo. */
function capturadoDe(p: Pick<WeighIn, 'capturado'>): (Capturado & { unidad: UnidadPeso }) | null {
  const c = p.capturado as Capturado | null | undefined;
  if (c === null || c === undefined || !Number.isFinite(c.valor) || !esUnidadPeso(c.unidad)) return null;
  return { valor: c.valor, unidad: c.unidad };
}

function NoEsta({ texto, volver }: { texto: string; volver: { a: string; texto: string; etiqueta: string } }) {
  return (
    <div>
      <PageHeader titulo="No está." volver={volver} />
      <EstadoVacio titulo={texto} descripcion="Puede que se haya borrado." />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registrar o corregir un pesaje (Req. 15; tarea 16.2)
// ---------------------------------------------------------------------------

export function PesajePage() {
  const { id } = useParams();
  const [parametros] = useSearchParams();
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const basculas = useLiveQuery(() => leerBasculasConPesajes(), []);
  const pesajes = useLiveQuery(() => leerPesajes(), []);
  // El id y la hora del pesaje nuevo se fijan al abrir: dos toques no duplican (Req. 1.9)
  const [abierto] = useState(() => ({ id: nuevoId('pes'), hora: horaActual() }));
  if (ajustes === undefined || basculas === undefined || pesajes === undefined) return null;

  const existente = id === undefined ? null : (pesajes.find((p) => p.id === id) ?? null);
  if (id !== undefined && existente === null) {
    return <NoEsta texto="Ese pesaje ya no existe." volver={{ a: '/cuerpo/pesajes', texto: 'Pesajes', etiqueta: 'Volver a tus pesajes' }} />;
  }
  return (
    <FormularioPesaje
      key={existente?.id ?? abierto.id}
      existente={existente}
      nuevoId={abierto.id}
      horaInicial={abierto.hora}
      hoy={hoy}
      ajustes={ajustes}
      basculas={basculas.map((b) => b.bascula)}
      pesajes={pesajes}
      volverAHoy={parametros.get('desde') === 'hoy'}
    />
  );
}

interface PropsFormulario {
  existente: WeighIn | null;
  nuevoId: string;
  horaInicial: string;
  hoy: FechaLocal;
  ajustes: Settings;
  basculas: readonly Scale[];
  pesajes: readonly WeighIn[];
  volverAHoy: boolean;
}

/** La unidad que se propone (design.md, 5.3): la de la báscula; sin báscula, la del último pesaje; si no, la preferida. */
function unidadPropuesta(bascula: Scale | null, pesajes: readonly WeighIn[], preferida: UnidadPeso): UnidadPeso {
  if (bascula !== null) return bascula.unidad;
  const ultimo = pesajes[0];
  return (ultimo === undefined ? null : capturadoDe(ultimo)?.unidad) ?? preferida;
}

function FormularioPesaje({ existente, nuevoId: idNuevo, horaInicial, hoy, ajustes, basculas, pesajes, volverAHoy }: PropsFormulario) {
  const navegar = useNavigate();
  // Al pesarse se propone la última báscula usada (Req. 14.3), si sigue activa
  const ultima = basculas.find((b) => b.id === ajustes.ultimaBasculaId && !b.archivada) ?? null;
  const capturado = existente === null ? null : capturadoDe(existente);
  // Su báscula ya no está (por ejemplo, de un respaldo): queda sin báscula, que sí se puede guardar.
  // Antes se quedaba con ella y "Guardar" fallaba sin dejar elegir otra (revisión de la 0.4.0)
  const sinSuBascula = existente !== null && existente.scaleId !== null && !basculas.some((b) => b.id === existente.scaleId);
  const [scaleId, setScaleId] = useState<string | null>(
    existente === null ? (ultima?.id ?? null) : sinSuBascula ? null : existente.scaleId,
  );
  const unidadInicial: UnidadPeso =
    existente === null ? unidadPropuesta(ultima, pesajes, ajustes.unidadPeso) : (capturado?.unidad ?? ajustes.unidadPeso);
  const [unidad, setUnidad] = useState<UnidadPeso>(unidadInicial);
  const textoInicial = existente === null ? '' : paraCampo(capturado?.valor ?? desdeKg(existente.pesoKg, ajustes.unidadPeso));
  const [texto, setTexto] = useState(textoInicial);
  const [fecha, setFecha] = useState<FechaLocal>(existente?.fecha ?? hoy);
  const [hora, setHora] = useState(existente?.hora ?? horaInicial);
  const [nota, setNota] = useState(typeof existente?.nota === 'string' ? existente.nota : '');
  const [errorPeso, setErrorPeso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pregunta, setPregunta] = useState<(PreguntaDeUnidad & { referenciaKg: number }) | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  // Cualquier cambio vuelve a revisar la unidad al guardar
  const cambio = <T,>(fijar: (v: T) => void) => (v: T) => {
    fijar(v);
    setPregunta(null);
    setErrorPeso(null);
    setError(null);
  };

  // Las activas, y la archivada de este pesaje si era de una
  const opciones = basculas.filter((b) => !b.archivada || b.id === existente?.scaleId);

  async function guardar(unidadElegida?: UnidadPeso) {
    if (guardando) return;
    const unidadFinal = unidadElegida ?? unidad;
    const leido = validarPeso(texto, unidadFinal);
    if (!leido.valido) {
      setErrorPeso(leido.error);
      return;
    }
    // El peso sin tocar se guarda tal como estaba (sin redondear) y no vuelve a preguntar la unidad
    const pesoSinCambiar = existente !== null && texto.trim() === textoInicial && unidadFinal === unidadInicial;
    const valor = pesoSinCambiar && capturado !== null ? capturado.valor : leido.valor;
    // ¿Unidad equivocada? Se pregunta antes de guardar (Req. 15.4); con la respuesta se guarda tal cual
    if (unidadElegida === undefined && !pesoSinCambiar) {
      const referenciaKg = referenciaParaUnidad(pesajes, ajustes.basculaTendenciaId, fecha, existente?.id ?? null);
      const p = preguntaDeUnidad(leido.valor, unidadFinal, referenciaKg);
      if (p !== null && referenciaKg !== null) {
        setPregunta({ ...p, referenciaKg });
        return;
      }
    }
    setGuardando(true);
    setError(null);
    try {
      await guardarPesaje({ id: existente?.id ?? idNuevo, fecha, hora, valor, unidad: unidadFinal, scaleId, nota }, hoy);
      void navegar(existente !== null ? '/cuerpo/pesajes' : volverAHoy ? '/' : '/cuerpo', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function borrar() {
    if (existente === null) return;
    setGuardando(true);
    try {
      await borrarPesaje(existente.id);
      void navegar('/cuerpo/pesajes', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={fechaDelDia(fecha)}
        titulo={existente === null ? 'Registrar peso.' : 'Corregir pesaje.'}
        descripcion={
          existente === null
            ? 'En la unidad de la báscula que tengas enfrente. Se guarda tal como lo escribas.'
            : 'Corregirlo cambia tu tendencia; lo demás no se toca.'
        }
        volver={
          existente !== null
            ? { a: '/cuerpo/pesajes', texto: 'Pesajes', etiqueta: 'Volver a tus pesajes' }
            : volverAHoy
              ? { a: '/', texto: 'Hoy', etiqueta: 'Volver a Hoy' }
              : VOLVER_A_CUERPO
        }
      />

      {existente !== null && capturado !== null && (
        <p className="-mt-3 mb-5 text-[13px] text-texto-suave">
          Capturado: {formatearCapturado(capturado)}
          {capturado.unidad !== ajustes.unidadPeso && ` · se ve como ${formatearPeso(existente.pesoKg, ajustes.unidadPeso)}`}
        </p>
      )}

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        {opciones.length > 0 ? (
          <Opciones
            etiqueta="Báscula"
            valor={scaleId ?? ''}
            opciones={[
              { valor: '', etiqueta: 'Sin báscula' },
              ...opciones.map((b) => ({ valor: b.id, etiqueta: `${b.nombre} · ${b.unidad}${b.archivada ? ' (archivada)' : ''}` })),
            ]}
            onCambio={cambio((v: string) => {
              const elegida = opciones.find((b) => b.id === v) ?? null;
              setScaleId(elegida?.id ?? null);
              // La unidad de la báscula, que todavía se puede cambiar (Req. 14.2)
              if (elegida !== null) setUnidad(elegida.unidad);
            })}
          />
        ) : (
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Sin básculas guardadas: el peso queda sin báscula.{' '}
            <Link to="/cuerpo/basculas" className="font-medium text-marca underline underline-offset-2">
              Agregar básculas
            </Link>
          </p>
        )}
        {sinSuBascula && (
          <p className="-mt-2 text-[12px] leading-relaxed text-texto-suave">
            La báscula de este pesaje ya no está guardada: al guardar queda sin báscula, salvo que elijas otra.
          </p>
        )}

        <UnitField
          etiqueta="Peso"
          magnitud="peso"
          valor={texto}
          onCambio={cambio(setTexto)}
          unidad={unidad}
          onCambioUnidad={cambio(setUnidad)}
          unidadPreferida={ajustes.unidadPeso}
          uso="corporal"
          error={errorPeso}
          placeholder={unidad === 'lb' ? '154.3' : '70.0'}
          autoFocus={existente === null}
        />

        {/* Una debajo de otra: en el iPhone los campos de fecha y hora son anchos, y la hora lleva "Ahora" */}
        <DateField etiqueta="Fecha" valor={fecha} onCambio={cambio(setFecha)} max={hoy} />
        <CampoHora etiqueta="Hora" valor={hora} onCambio={cambio(setHora)} />

        <TextField
          etiqueta="Nota (opcional)"
          valor={nota}
          onCambio={cambio(setNota)}
          maxLength={NOTA_PESAJE_MAX}
          placeholder="Por ejemplo: después de entrenar"
        />

        {pregunta !== null && (
          <Banner tono="aviso" titulo={textoPreguntaDeUnidad(pregunta)}>
            <p>
              {`En ${pregunta.escrita} quedaría a más de ${DIFERENCIA_UNIDAD_KG} kg de tu peso reciente (${formatearPeso(pregunta.referenciaKg, ajustes.unidadPeso)}); en ${pregunta.otra}, cerca. Elige cuál es: se guarda con la que elijas.`}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                disabled={guardando}
                onClick={() => {
                  setUnidad(pregunta.otra);
                  void guardar(pregunta.otra);
                }}
              >
                Usar {pregunta.otra}
              </Button>
              <Button variante="secundario" disabled={guardando} onClick={() => void guardar(pregunta.escrita)}>
                Usar {pregunta.escrita}
              </Button>
            </div>
          </Banner>
        )}

        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}

        {pregunta === null && (
          <Button type="submit" disabled={guardando}>
            {guardando ? 'Guardando…' : existente === null ? 'Guardar peso' : 'Guardar cambios'}
          </Button>
        )}
      </form>

      {existente !== null && (
        <div className="mt-6 space-y-2">
          {confirmarBorrar ? (
            <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
              <p className="text-[13px] text-texto">¿Borrar este pesaje? No se puede deshacer y tu tendencia cambiará.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variante="peligro" disabled={guardando} onClick={() => void borrar()}>
                  Sí, borrar
                </Button>
                <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
              Borrar pesaje
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista de pesajes (Req. 15.5)
// ---------------------------------------------------------------------------

export function PesajesPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const pesajes = useLiveQuery(() => leerPesajes(), []);
  const ilegibles = useLiveQuery(() => contarPesajesIlegibles(), []);
  const basculas = useLiveQuery(() => leerBasculasConPesajes(), []);
  if (ajustes === undefined || pesajes === undefined || ilegibles === undefined || basculas === undefined) return null;

  const lista = basculas.map((b) => b.bascula);
  const grupos: { mes: string; pesajes: WeighIn[] }[] = [];
  for (const p of pesajes) {
    const mes = etiquetaMes(p.fecha);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo !== undefined && ultimo.mes === mes) ultimo.pesajes.push(p);
    else grupos.push({ mes, pesajes: [p] });
  }

  return (
    <div>
      <PageHeader
        titulo="Tus pesajes."
        descripcion="Del más reciente al más antiguo. Toca uno para corregirlo o borrarlo."
        volver={VOLVER_A_CUERPO}
      />
      <AvisoIlegibles cantidad={ilegibles} />
      <BotonEnlace to="/cuerpo/pesaje/nuevo" icono={Plus}>
        Registrar peso
      </BotonEnlace>

      {pesajes.length === 0 ? (
        <div className="mt-6">
          <EstadoVacio titulo="Todavía no hay pesajes." descripcion="El primero que registres aparecerá aquí." />
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {grupos.map((g) => (
            <Seccion
              key={g.mes}
              titulo={`${g.mes.charAt(0).toLocaleUpperCase('es-MX')}${g.mes.slice(1)}`}
              extra={g.pesajes.length === 1 ? '1 pesaje' : `${g.pesajes.length} pesajes`}
            >
              <Filas>
                {g.pesajes.map((p) => {
                  const c = capturadoDe(p);
                  const escrito = c !== null && c.unidad !== ajustes.unidadPeso ? `escrito ${formatearCapturado(c)}` : null;
                  return (
                    <Fila
                      key={p.id}
                      titulo={formatearPeso(p.pesoKg, ajustes.unidadPeso)}
                      detalle={[cuandoYDonde(p, lista), escrito, p.nota].filter(Boolean).join(' · ')}
                      a={`/cuerpo/pesaje/${p.id}`}
                    />
                  );
                })}
              </Filas>
            </Seccion>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Básculas (Req. 14; tarea 15)
// ---------------------------------------------------------------------------

const textoPesajes = (n: number) => (n === 1 ? '1 pesaje' : `${n} pesajes`);

export function BasculasPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const datos = useLiveQuery(() => leerBasculasConPesajes(), []);
  const [error, setError] = useState<string | null>(null);
  if (ajustes === undefined || datos === undefined) return null;

  const activas = datos.filter((d) => !d.bascula.archivada);
  const archivadas = datos.filter((d) => d.bascula.archivada);
  // Si la que cuenta quedó archivada (por ejemplo, en un respaldo), también se muestra
  const paraTendencia = datos.filter((d) => !d.bascula.archivada || d.bascula.id === ajustes.basculaTendenciaId);

  const fila = ({ bascula, pesajes }: BasculaConPesajes) => (
    <Fila
      key={bascula.id}
      titulo={bascula.nombre}
      detalle={`${bascula.unidad} · ${textoPesajes(pesajes)}${bascula.id === ajustes.basculaTendenciaId ? ' · cuenta para la tendencia' : ''}`}
      a={`/cuerpo/basculas/${bascula.id}`}
    />
  );

  return (
    <div className="space-y-7">
      <PageHeader
        titulo="Tus básculas."
        descripcion="Cada una con su unidad: al elegirla al pesarte, el peso se escribe en esa unidad, y todavía la puedes cambiar."
        volver={VOLVER_A_CUERPO}
      />

      {paraTendencia.length > 0 && (
        <Seccion
          titulo="Para tu tendencia"
          descripcion="Qué pesajes cuentan para la tendencia de 7 días. Con todas, también cuentan los que no tienen báscula."
        >
          <Opciones
            etiqueta="Básculas que cuentan"
            valor={ajustes.basculaTendenciaId ?? ''}
            opciones={[
              { valor: '', etiqueta: 'Todas' },
              ...paraTendencia.map((d) => ({ valor: d.bascula.id, etiqueta: `Solo ${d.bascula.nombre}` })),
            ]}
            onCambio={(v) => {
              setError(null);
              elegirBasculaDeTendencia(v === '' ? null : v).catch((e: unknown) => setError(mensajeDeError(e)));
            }}
          />
          {error !== null && (
            <Banner tono="alerta" titulo="No se cambió">
              {error}
            </Banner>
          )}
        </Seccion>
      )}

      <Seccion titulo="Tus básculas" extra={activas.length === 1 ? '1 báscula' : `${activas.length} básculas`}>
        {activas.length === 0 ? (
          <p className="text-[13px] leading-relaxed text-texto-suave">
            Todavía no tienes básculas. Por ejemplo, «Casa» en lb y «Gym» en kg. Pesarte sin báscula también se puede.
          </p>
        ) : (
          <Filas>{activas.map(fila)}</Filas>
        )}
        <BotonEnlace to="/cuerpo/basculas/nueva" variante="secundario" icono={Plus}>
          Agregar báscula
        </BotonEnlace>
      </Seccion>

      {archivadas.length > 0 && (
        <Seccion titulo="Archivadas" descripcion="No se ofrecen al pesarte; sus pesajes se conservan.">
          <Filas>{archivadas.map(fila)}</Filas>
        </Seccion>
      )}
    </div>
  );
}

export function BasculaPage() {
  const { id } = useParams();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const datos = useLiveQuery(() => leerBasculasConPesajes(), []);
  const [abierto] = useState(() => nuevoId('bas'));
  if (ajustes === undefined || datos === undefined) return null;
  const actual = id === undefined ? null : (datos.find((d) => d.bascula.id === id) ?? null);
  if (id !== undefined && actual === null) {
    return <NoEsta texto="Esa báscula ya no existe." volver={{ a: '/cuerpo/basculas', texto: 'Básculas', etiqueta: 'Volver a tus básculas' }} />;
  }
  return <FormularioBascula key={actual?.bascula.id ?? abierto} actual={actual} idNuevo={abierto} ajustes={ajustes} />;
}

function FormularioBascula({ actual, idNuevo, ajustes }: { actual: BasculaConPesajes | null; idNuevo: string; ajustes: Settings }) {
  const navegar = useNavigate();
  const [nombre, setNombre] = useState(actual?.bascula.nombre ?? '');
  const [unidad, setUnidad] = useState<UnidadPeso>(actual?.bascula.unidad ?? ajustes.unidadPeso);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const volver = { a: '/cuerpo/basculas', texto: 'Básculas', etiqueta: 'Volver a tus básculas' };

  async function hacer(accion: () => Promise<unknown>, despues: string | null) {
    setOcupado(true);
    setError(null);
    try {
      await accion();
      if (despues !== null) void navegar(despues, { replace: true });
      else setOcupado(false);
    } catch (e) {
      setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  const bascula = actual?.bascula ?? null;
  const pesajes = actual?.pesajes ?? 0;
  const cuenta = bascula !== null && bascula.id === ajustes.basculaTendenciaId;

  return (
    <div>
      <PageHeader
        titulo={bascula === null ? 'Nueva báscula.' : bascula.nombre}
        descripcion={
          bascula === null
            ? 'Su nombre y la unidad que marca. Al pesarte, la eliges y el peso se escribe en esa unidad.'
            : `${textoPesajes(pesajes)}${bascula.archivada ? ' · archivada' : ''}${cuenta ? ' · cuenta para tu tendencia' : ''}`
        }
        volver={volver}
      />

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (ocupado) return;
          void hacer(
            () => (bascula === null ? crearBascula(idNuevo, { nombre, unidad }) : editarBascula(bascula.id, { nombre, unidad })),
            '/cuerpo/basculas',
          );
        }}
      >
        <TextField etiqueta="Nombre" valor={nombre} onCambio={setNombre} maxLength={NOMBRE_BASCULA_MAX} placeholder="Casa" autoFocus={bascula === null} />
        <Segmentado
          etiqueta="Unidad que marca"
          etiquetaVisible
          valor={unidad}
          opciones={UNIDADES_PESO.map((u) => ({ valor: u, etiqueta: u }))}
          onCambio={setUnidad}
        />
        {bascula !== null && (
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Cambiar la unidad no cambia los pesajes que ya tienes: cada uno conserva lo que escribiste.
          </p>
        )}
        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        )}
        <Button type="submit" disabled={ocupado}>
          {bascula === null ? 'Guardar báscula' : 'Guardar cambios'}
        </Button>
      </form>

      {bascula !== null && (
        <div className="mt-8 space-y-5">
          <div className="space-y-2">
            <p className="text-[12px] leading-relaxed text-texto-suave">
              {bascula.archivada
                ? 'Archivada: no se ofrece al pesarte. Al desarchivarla vuelve a ofrecerse.'
                : `Archivada no se ofrece al pesarte; ${pesajes === 0 ? 'no tiene pesajes' : `sus ${textoPesajes(pesajes)} se conservan y siguen en la lista`}.${cuenta ? ' Ahora solo cuenta esta para tu tendencia: al archivarla contarán todas.' : ''}`}
            </p>
            <Button
              variante="secundario"
              icono={bascula.archivada ? ArchiveRestore : Archive}
              disabled={ocupado}
              onClick={() => void hacer(() => archivarBascula(bascula.id, !bascula.archivada), null)}
            >
              {bascula.archivada ? 'Desarchivar' : 'Archivar'}
            </Button>
          </div>

          {pesajes > 0 ? (
            <p className="text-[12px] leading-relaxed text-texto-suave">
              Tiene {textoPesajes(pesajes)}: en lugar de borrarla, archívala para conservarlos.
            </p>
          ) : confirmarBorrar ? (
            <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
              <p className="text-[13px] text-texto">¿Borrar esta báscula? No tiene pesajes.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variante="peligro" disabled={ocupado} onClick={() => void hacer(() => borrarBascula(bascula.id), '/cuerpo/basculas')}>
                  Sí, borrar
                </Button>
                <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
              Borrar báscula
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estatura para el IMC (Req. 16.7; formulario depurado, design.md, 7.3)
// ---------------------------------------------------------------------------

export function EstaturaPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  if (ajustes === undefined) return null;
  return <FormularioEstatura key={ajustes.perfilId} ajustes={ajustes} />;
}

function FormularioEstatura({ ajustes }: { ajustes: Settings }) {
  const navegar = useNavigate();
  const capturada = ajustes.estaturaCapturada;
  const escrita = capturada !== null && esUnidadLongitud(capturada.unidad) && Number.isFinite(capturada.valor) ? capturada : null;
  const unidadInicial: UnidadLongitud = escrita !== null && esUnidadLongitud(escrita.unidad) ? escrita.unidad : ajustes.unidadLongitud;
  const textoInicial =
    escrita !== null
      ? paraCampo(escrita.valor)
      : ajustes.estaturaCm !== null
        ? formatearNumero(desdeCm(ajustes.estaturaCm, ajustes.unidadLongitud), { decimales: 1, fijos: false }).replace(/,/g, '')
        : '';
  const [unidad, setUnidad] = useState<UnidadLongitud>(unidadInicial);
  const [texto, setTexto] = useState(textoInicial);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    // Sin cambios no se escribe nada: la estatura queda exactamente como estaba.
    // Antes, abierta en otra unidad (64.6 in) y guardada, pasaba de 164 a 164.08 cm (revisión de la 0.4.0)
    if (texto.trim() === textoInicial && unidad === unidadInicial) {
      void navegar('/cuerpo', { replace: true });
      return;
    }
    const r = validarEstatura(texto, unidad);
    if (!r.valido) {
      setError(r.error);
      return;
    }
    setGuardando(true);
    try {
      await actualizarSettings(r.valor === null ? { estaturaCm: null, estaturaCapturada: null } : r.valor);
      void navegar('/cuerpo', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        titulo="Tu estatura."
        descripcion="Solo para calcular tu IMC. Sin estatura, el IMC no se muestra."
        volver={VOLVER_A_CUERPO}
      />
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!guardando) void guardar();
        }}
      >
        <UnitField
          etiqueta="Estatura"
          magnitud="longitud"
          valor={texto}
          onCambio={(v) => {
            setTexto(v);
            setError(null);
          }}
          unidad={unidad}
          onCambioUnidad={(u) => {
            setUnidad(u);
            setError(null);
          }}
          unidadPreferida={ajustes.unidadLongitud}
          error={error}
          placeholder={unidad === 'in' ? '63' : '160'}
          {...(ajustes.estaturaCm !== null ? { ayuda: 'Para quitarla, borra el número y guarda.' } : {})}
        />
        <Button type="submit" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar estatura'}
        </Button>
      </form>
    </div>
  );
}
